import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { useCallback } from "react";

import { API_URL } from "constants/api";
import useErrorModalStatusContext from "hooks/useErrorModalStatusContext";
import useFirebaseContext from "hooks/useFirebaseContext";

const TIMEOUT_MS = 8000;

// タイムアウト付きのPromise実行ユーティリティ
const withTimeout = <T>(promiseFactory: () => Promise<T>, timeoutMs: number, onTimeout?: () => void): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      onTimeout?.(); // タイムアウト時の副作用を実行
      reject(new Error("timeout"));
    }, timeoutMs);

    promiseFactory()
      .then((res) => {
        clearTimeout(timer);
        resolve(res);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
};

const reload = () => window.location.reload();

const useClient = () => {
  const { auth } = useFirebaseContext();
  const { showErrorModal } = useErrorModalStatusContext();

  const showRetryModal = () => showErrorModal({ httpMethod: "timeout", onPostClose: reload });

  const client = useCallback(
    async <T, D>(config: AxiosRequestConfig): Promise<AxiosResponse<T, D>> => {
      const token = await withTimeout(
        () => auth.currentUser?.getIdToken() ?? Promise.reject(new Error("401 Unauthorized")),
        TIMEOUT_MS,
        // ← タイムアウト時にモーダルを開く
        showRetryModal
      );
      if (!token) throw new Error("401 Unauthorized");

      return await withTimeout(
        () =>
          axios.request<T>({
            method: config.method,
            url: `${API_URL}${config.url}`,
            headers: {
              accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
            params: config?.params ?? undefined,
            data: config?.data ?? undefined,
          }),
        TIMEOUT_MS,
        // ← タイムアウト時にモーダルを開く
        showRetryModal
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth?.currentUser]
  );

  return { client };
};

export default useClient;
